import React from 'react';
import { Card, TextField, RadioGroup, Button } from '@dopomogai/ui';
import type { CatalogCategory, FeaturesFilterState } from '@dopomogai/types';
import { X } from 'lucide-react';

type Props = {
  categories: CatalogCategory[];
  filters: FeaturesFilterState;
  onFilterChange: (newFilters: Partial<FeaturesFilterState>) => void;
  onClearFilters: () => void;
};

export function FeaturesFilterSidebar({ categories, filters, onFilterChange, onClearFilters }: Props) {
  // Debounced search local state for better UX and fewer requests
  const [localQ, setLocalQ] = React.useState(filters.q || '');
  React.useEffect(() => setLocalQ(filters.q || ''), [filters.q]);
  React.useEffect(() => {
    const t = setTimeout(() => onFilterChange({ q: localQ || undefined }), 350);
    return () => clearTimeout(t);
  }, [localQ]); // eslint-disable-line react-hooks/exhaustive-deps

  const hasActiveFilters =
    !!(filters.q || (filters.categories && filters.categories.length > 0) || filters.tier_max || filters.maturity);

  return (
    <div className="sticky top-24">
      <Card noPadding>
        <div className="p-4 border-b-2 border-black">
          <h3 className="text-lg font-extrabold text-slate-900">Filters</h3>
        </div>

        <div className="p-4">
          <div className="space-y-5 max-h-[calc(100vh-8rem)] overflow-y-auto pr-2">
            <TextField
              label="Search features"
              value={localQ}
              onChange={(e) => setLocalQ(e.target.value)}
              placeholder="e.g., 'Salesforce'"
            />

            <RadioGroup
              label="Category"
              value={(filters.categories && filters.categories[0]) || ''}
              onChange={(v) => onFilterChange({ categories: v ? [String(v)] : [] })}
              options={[
                { label: 'Any', value: '' },
                ...categories.map(c => ({ value: c.id, label: `${c.label} (${c.count})` })),
              ]}
            />

            <RadioGroup
              label="Max Tier"
              value={filters.tier_max}
              onChange={(v) => onFilterChange({ tier_max: (v || undefined) as any })}
              options={[
                { label: 'Any', value: '' },
                { label: 'Trainee', value: 'trainee' },
                { label: 'Junior', value: 'junior' },
                { label: 'Middle', value: 'middle' },
                { label: 'Senior', value: 'senior' },
              ]}
            />

            <RadioGroup
              label="Maturity"
              value={filters.maturity}
              onChange={(v) => onFilterChange({ maturity: (v || undefined) as any })}
              options={[
                { label: 'Any', value: '' },
                { label: 'Shipped', value: 'shipped_now' },
                { label: '1 Week', value: '1w' },
                { label: '3 Weeks', value: '3w' },
                { label: 'Research', value: 'research' },
              ]}
            />

            {hasActiveFilters && (
              <Button
                variant="ghost"
                size="sm"
                icon={<X size={14} />}
                text="Clear Filters"
                onClick={onClearFilters}
                className="focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-emerald-500"
              />
            )}
          </div>
        </div>
      </Card>
    </div>
  );
}
